import React, { useMemo } from 'react';
import { Alert, Box, Button, Link as MuiLink, Typography } from '@mui/material';
import { BusinessOutlined, FolderOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatDate } from '../utils/dateUtils';
import { customerPath, projectPath } from '../utils/navigation';
import { EmptyState, IconTile, ListSkeleton } from './pagekit';

/**
 * @typedef {'project' | 'customer'} ActivityKind
 */

/**
 * @typedef {object} ActivityItem
 * @property {ActivityKind} kind - Entity type.
 * @property {string} id - Record id.
 * @property {string} title - Primary label.
 * @property {string} [subtitle] - Secondary line.
 * @property {string} href - Router path.
 * @property {number} at - Sort key (epoch ms).
 */

/**
 * Merges projects and customers into a single sorted activity list (templates have their own card).
 * @param {object[]} projects - Projects from AppContext (`createdAt` field).
 * @param {object[]} customers - Customers from AppContext (`createdAt` field).
 * @param {number} [limit=6] - Max items to return.
 * @returns {ActivityItem[]} Newest-first activity rows.
 */
function buildActivityItems(projects, customers, limit = 6) {
  /** @type {ActivityItem[]} */
  const rows = [];

  (projects || []).forEach((p) => {
    const customerName = p.customer?.name;
    rows.push({
      kind: 'project',
      id: p.id,
      title: p.name || 'Untitled project',
      subtitle: customerName ? `Project in ${customerName}` : 'Project created',
      href: projectPath(p.id),
      at: new Date(p.createdAt || 0).getTime(),
    });
  });

  (customers || []).forEach((c) => {
    rows.push({
      kind: 'customer',
      id: c.id,
      title: c.name || 'Untitled customer',
      subtitle: 'Customer added',
      href: customerPath(c.id),
      at: new Date(c.createdAt || 0).getTime(),
    });
  });

  rows.sort((a, b) => {
    const d = b.at - a.at;
    if (d !== 0) return d;
    return `${a.kind}-${a.id}`.localeCompare(`${b.kind}-${b.id}`);
  });

  return rows.slice(0, limit);
}

/**
 * Recent activity card for the home dashboard (newest customers and projects).
 * @param {object} props - Component props.
 * @param {boolean} props.loading - When true, skeleton rows are shown.
 * @param {boolean} props.canManageTemplates - Offer the templates link in the empty state.
 * @returns {JSX.Element} Card.
 */
export default function RecentActivityFeed({ loading, canManageTemplates }) {
  const { state: appState } = useApp();

  const items = useMemo(
    () => buildActivityItems(appState.projects || [], appState.customers || []),
    [appState.projects, appState.customers]
  );

  const projectCount = (appState.projects || []).length;
  const customerCount = (appState.customers || []).length;
  const showProjectHint = projectCount === 0 && customerCount > 0;
  const showCustomerHint = customerCount === 0 && projectCount > 0;
  const showDualEmptyHints = projectCount === 0 && customerCount === 0;

  return (
    <Box
      component="section"
      aria-labelledby="home-activity-heading"
      sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', py: 1, height: '100%', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 1.25, pb: 1 }}>
        <Typography id="home-activity-heading" component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>Recent activity</Typography>
        <MuiLink component={RouterLink} to="/projects" underline="hover" sx={{ fontSize: 13, fontWeight: 500 }}>View all</MuiLink>
      </Box>

      {loading ? (
        <ListSkeleton rows={4} label="Loading recent activity" />
      ) : (
        <>
          {showProjectHint && (
            <Alert
              severity="info"
              sx={{ mx: 2.5, my: 1 }}
              action={<Button component={RouterLink} to="/projects" color="inherit" size="small">New project</Button>}
            >
              You have customers but no projects yet. Projects hold sessions, requirements and documents.
            </Alert>
          )}
          {showCustomerHint && (
            <Alert
              severity="info"
              sx={{ mx: 2.5, my: 1 }}
              action={<Button component={RouterLink} to="/customers" color="inherit" size="small">Customers</Button>}
            >
              Add a customer so projects are grouped under the right client.
            </Alert>
          )}

          {items.length === 0 ? (
            showDualEmptyHints ? (
              <EmptyState
                inky
                compact
                title="Your workspace is ready"
                description="Add a customer, create a project for each initiative, then record a meeting. Sessions and documents show up here as you go."
                actions={(
                  <>
                    <Button variant="contained" component={RouterLink} to="/customers">Add a customer</Button>
                    <Button variant="outlined" component={RouterLink} to="/projects">New project</Button>
                    {canManageTemplates && <Button component={RouterLink} to="/templates">Browse templates</Button>}
                  </>
                )}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>No activity to show yet.</Typography>
            )
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {items.map((item) => (
                <Box component="li" key={`${item.kind}-${item.id}`}>
                  <Box
                    component={RouterLink}
                    to={item.href}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2.5,
                      py: 1.25,
                      textDecoration: 'none',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'var(--q-bg-subtle)' },
                      '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: -2 },
                    }}
                  >
                    <IconTile tone="neutral" size={32}>
                      {item.kind === 'customer' ? <BusinessOutlined /> : <FolderOutlined />}
                    </IconTile>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'var(--q-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.subtitle}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: 'var(--q-faint)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDate(new Date(item.at).toISOString())}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
